// Trust Domain Details Components

import {toSentenceCase} from "../_ui/_utils/useStringUtils";

/**
 * Trust Level Badge Component (shared)
 * Displays trust level with appropriate styling and description
 */
export const ApckTrustLevelBadge = {
    props: {
        level: {
            type: Number,
            default: 0
        },
        description: {
            type: String,
            default: ''
        },
        showDescription: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        trustLevelClass: function() {
            const levelClasses = {
                5: 'trust-level-critical',
                4: 'trust-level-high',
                3: 'trust-level-medium',
                2: 'trust-level-low',
                1: 'trust-level-info',
                0: 'trust-level-investigate'
            };
            return levelClasses[this.level] || 'trust-level-unknown';
        },
        trustLevelName: function() {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[this.level] || 'UNKNOWN';
        }
    },
    template: `
        <div class="trust-level-badge">
            <span :class="['trust-level', trustLevelClass]" :title="description">
                {{ level }} - {{ trustLevelName }}
            </span>
            <div v-if="showDescription && description" class="trust-description">
                {{ description }}
            </div>
        </div>
    `
};

/**
 * Country Information Component (shared)
 * Displays country flag and name from GeoIP data
 */
export const ApckCountryInfo = {
    props: {
        countryInfo: {
            type: Object,
            default: null
        }
    },
    computed: {
        flagEmoji: function() {
            if (!this.countryInfo?.iso_code) return '';
            // Convert ISO code to flag emoji using regional indicator symbols
            const code = this.countryInfo.iso_code.toUpperCase();
            return String.fromCodePoint(
                ...[...code].map(c => c.charCodeAt(0) + 127397)
            );
        }
    },
    template: `
        <div v-if="countryInfo" class="country-info">
            <span class="country-flag" :title="countryInfo.name">
                {{ flagEmoji }}
            </span>
            <span class="country-name">
                {{ countryInfo.name }}
            </span>
            <span class="country-code">
                ({{ countryInfo.iso_code }})
            </span>
        </div>
    `
};

/**
 * Third-party Favicon Component (shared)
 * Displays favicon with fallback to origin URL favicon
 */
export const ApckThirdpartyFavicon = {
    props: {
        favicon: {
            type: Object,
            default: function() {
                return {};
            }
        },
        hostname: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: '24'
        }
    },
    data: function() {
        return {
            imageError: false
        };
    },
    computed: {
        faviconUrl: function() {
            // 'a=' is invalid base64, will trigger error and show default icon if no favicon available
            return `data:${this.favicon?.contentType || 'image/x-icon'};base64,${this.favicon?.favicon_base64 || 'a='}`;
        }
    },
    methods: {
        handleImageError(event) {
            this.imageError = true;
            // Replace the img element with a Font Awesome icon
            const icon = document.createElement('i')
            icon.className = 'fa fa-globe trust-icon flex dir-column align-center justify-center fa-xl'
            icon.title = event.target.title
            event.target.parentNode.replaceChild(icon, event.target)
        },
    },
    template: `
        <img
            class="trust-favicon"
            :src="faviconUrl"
            :alt="hostname + ' favicon'"
            :title="hostname"
            :width="size"
            :height="size"
            @error="handleImageError"
        />
    `
};

/**
 * Statistics Panel Component
 * Shows domain statistics in a visual format
 */
export const ApckDomainStatistics = {
    props: {
        statistics: {
            type: Object,
            required: true
        }
    },
    computed: {
        trustLevelChartData: function() {
            const levels = this.statistics.trust_levels || {};
            return Object.entries(levels)
                //.filter(([level, count]) => count > 0)
                .map(([level, count]) => ({
                    level: parseInt(level),
                    count: count,
                    name: this.getTrustLevelName(parseInt(level))
                }))
                .sort((a, b) => b.level - a.level);
        },
        topCountries: function () {
            const countries = this.statistics.countries || {};
            return Object.entries(countries)
                .map(([iso_code, data]) => ({
                    iso_code: iso_code,
                    name: data.name,
                    count: data.count
                }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
        },
        topServiceTypes: function () {
            const services = this.statistics.service_types || {};
            return Object.entries(services)
                .map(([name, count]) => ({ name, count }))
                .slice(0, 5);
        },
        vulnerabilityTotal: function() {
            const vulns = this.statistics.vulnerabilities || {};
            return Object.values(vulns).reduce((sum, count) => sum + count, 0);
        }
    },
    methods: {
        getTrustLevelName: function(level) {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[level] || 'UNKNOWN';
        },
        trustColor(level) {
            const colors = {
                5: 'red',
                4: 'orange',
                3: 'yellow',
                2: 'green',
                1: 'blue',
                0: ''
            };
            return colors[level] || '';
        },
        getFlagEmoji: function(isoCode) {
            if (!isoCode) return '';
            const code = isoCode.toUpperCase();
            return String.fromCodePoint(
                ...[...code].map(c => c.charCodeAt(0) + 127397)
            );
        }
    },
    template: `
        <ui-layout justify="stretch" wrap>
            <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                <ui-heading tag="h3" size="md">{{ statistics.total_thirdparties }}</ui-heading>

                <span class="text--secondary">Third-parties</span>
            </ui-panel-base>

            <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                <ui-heading tag="h3" size="md">{{topCountries.length }}</ui-heading>

                <span class="text--secondary">Countries</span>
            </ui-panel-base>

            <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                <ui-heading tag="h3" size="md">{{ vulnerabilityTotal }}</ui-heading>

                <span class="text--secondary">Vulnerabilities</span>
            </ui-panel-base>
       
            <ui-panel-base color="light-slate" direction="column" align="start" justify="start">
                <ui-heading tag="h3">Trust Levels</ui-heading>

                <ui-layout v-if="trustLevelChartData.length > 0" align="center" wrap>
                    <ui-layout v-for="item in trustLevelChartData" :key="item.name" gap="2" align="center">
                        <ui-badge :label="String(item.level) + ' - ' + item.name " fill :color="trustColor(item.level)" />

                        <ui-badge :label="String(item.count)" />
                    </ui-layout>
                </ui-layout>

                <span v-else>No trust relationships found</span>
            </ui-panel-base>

            <ui-panel-base color="light-slate" direction="column" align="start" justify="start">
                <ui-heading tag="h3">Countries Breakdown</ui-heading>

                <ui-layout v-if="topCountries.length > 0" align="center" wrap>
                    <ui-layout v-for="country in topCountries" :key="country.iso_code" gap="2" align="center">
                        <ui-badge :label="String(country.count)" />

                        <span class="country-flag">{{ getFlagEmoji(country.iso_code) }}</span>

                        <span class="country-name">{{ country.name }}</span>
                    </ui-layout>
                </ui-layout>

                <span v-else>No country data available</span>
            </ui-panel-base>

            <ui-panel-base color="light-slate" direction="column" align="start" justify="start">
                <ui-heading tag="h3">Service Types</ui-heading>

                <ui-layout v-if="topServiceTypes.length > 0" align="center" wrap>
                    <ui-layout v-for="service in topServiceTypes" :key="service.name" gap="2" align="center">
                        <ui-badge :label="String(service.count)" />

                        <span class="service-name">{{ service.name }}</span>
                    </ui-layout>
                </ui-layout>

                <span v-else>No service data available</span>
            </ui-panel-base>
        </ui-layout>
    `
};

/**
 * Third-party Item Component
 * Shows details about a third-party relationship with the domain
 */
export const ApckThirdpartyItem = {
    components: {
        'apck-trust-level-badge': ApckTrustLevelBadge,
        'apck-country-info': ApckCountryInfo,
        'apck-thirdparty-favicon': ApckThirdpartyFavicon
    },
    props: {
        thirdparty: {
            type: Object,
            required: true
        }
    },
    computed: {
        trustDetailsCount: function() {
            return this.thirdparty.trust_details?.length || 0;
        },
        vulnerabilityCount: function() {
            return this.thirdparty.vulnerabilities?.length || 0;
        },
        highestTrustLevel: function() {
            return this.thirdparty.highest_trust_level?.level || 0;
        },
        hasVulnerabilities: function() {
            return this.vulnerabilityCount > 0;
        },
        domainAge: function() {
            const age = this.thirdparty.domain_age;
            if (!age) return 'Unknown';
            if (age.days !== undefined) {
                if (age.days < 30) return `${age.days} days (New)`;
                if (age.days < 365) return `${Math.floor(age.days / 30)} months`;
            }
            if (age.years !== undefined) {
                return `${age.years} years`;
            }
            return 'Unknown';
        },
        trustCounts() {
            return `${ this.trustDetailsCount } trust${ this.trustDetailsCount !== 1 ? 's' : '' }`
        },
        vulnCounts() {
            return `${ this.vulnerabilityCount } vuln${ this.vulnerabilityCount !== 1 ? 's' : '' }`
        },
        trustLevelLabel() {
            return `${String(this.highestTrustLevel)} - ${this.getTrustLevelName(this.highestTrustLevel)}`
        },
        activeChecksConfigUrl() {
            return `/trust/active-monitor/config/add?url=${this.thirdparty.origin_url}&check_headers=true&check_changes=true`
        }
    },
    methods: {
        trustColor(level) {
            const colors = {
                5: 'red',
                4: 'orange',
                3: 'yellow',
                2: 'green',
                1: 'blue',
                0: ''
            };
            return colors[level] || '';
        },
        getTrustLevelName: function(level) {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[level] || 'UNKNOWN';
        },
        viewThirdpartyDetails: function() {
            // Navigate to third-party details view
            console.log('Navigate to third-party details:', this.thirdparty.thirdparty_id);
            window.location.href = `/trust/thirdparty/${this.thirdparty.thirdparty_id}/view`;
        },
        detailInstanceBadgeLabel(detail) {
            return `${String(detail.instances_count)} instance${detail.instances_count !== 1 ? 's' : ''}`
        }
    },
    template: `
        <ui-panel-accordion
            :style="{ 'border-left': hasVulnerabilities ? '3px solid #DC2626' : null }"
        >
            <template #trigger>
                <ui-layout align="center" class="grow" wrap>
                    <ui-layout align="center" class="grow" wrap>
                        <apck-thirdparty-favicon
                            :favicon="thirdparty.favicon"
                            :hostname="thirdparty.hostname"
                            size="24"
                        />
    
                        <ui-heading tag="h2">{{ thirdparty.hostname }}</ui-heading>
    
                        <span v-if="thirdparty.service_name">
                                {{ thirdparty.service_name }}
                            </span>
    
                        <span v-if="thirdparty.parent_domain">
                                ({{ thirdparty.parent_domain }})
                            </span>
                    </ui-layout>
    
                    <ui-layout gap="2" align="center" justify="end" wrap>
                        <ui-badge :label="trustLevelLabel" fill :color="trustColor(highestTrustLevel)" />
    
                        <ui-badge :label="trustCounts"/>
    
                        <ui-badge v-if="hasVulnerabilities" :label="vulnCounts" fill :color="vulnCounts > 0 ? 'red' : null" />
    
                        <ui-badge v-if="thirdparty.nxdomain" label="NX Domain : Takeover Risk" fill color="red" />
                    </ui-layout>
                </ui-layout>
            </template>

            <ui-layout
                direction="column"
                align="stretch"
            >
                <!-- Basic Information -->
                <ui-heading size="md" tag="h3">Information</ui-heading>

                <ui-layout align="center" wrap>
                    <ui-layout gap="2" align="center" wrap>
                        <span> Origin URL:</span>
                        <ui-button
                            v-if="thirdparty.origin_url"
                            :label="thirdparty.origin_url"
                            :url="thirdparty.origin_url"
                            type="link"
                            external
                            iconRight="fa-external-link"
                            size="sm"
                        />
                        <span v-else>Not available</span>
                    </ui-layout>
                    <ui-layout gap="2" align="center" wrap>
                        <span>Domain Age: </span>
                        <span>{{ domainAge }}</span>
                    </ui-layout>
                    <ui-layout gap="2" align="center" wrap>
                        <span>Location:</span>
                        <apck-country-info v-if="thirdparty.country_info" :country-info="thirdparty.country_info" />
                        <span v-else>Unknown</span>
                    </ui-layout>
                    <ui-layout gap="2" align="center" wrap>
                        <span>Trusted to CNAME:</span>
                        <ui-badge
                            fill
                            :label="thirdparty.i_am_trusted_to_cname ? 'Yes' : 'No'"
                            :color="thirdparty.i_am_trusted_to_cname ? 'yellow' : 'green'"
                        />
                    </ui-layout>
                </ui-layout>
            </ui-layout>

            <ui-layout v-if="thirdparty.cnames && thirdparty.cnames.length" direction="column">
                <ui-heading size="md" tag="h3">CNAMEs</ui-heading>
                <ui-layout gap="2">
                            <span v-for="cname in thirdparty.cnames" :key="cname">
                                {{ cname }}
                            </span>
                </ui-layout>
            </ui-layout>

            <!-- Vulnerabilities -->
            <ui-layout
                v-if="hasVulnerabilities"
                direction="column"
                align="stretch"
            >
                <ui-heading size="md" tag="h3">Vulnerabilities</ui-heading>

                <template v-if="thirdparty.trust_details && thirdparty.trust_details.length">
                    <ui-layout
                        v-for="vuln in thirdparty.vulnerabilities"
                        :key="vuln.id || vuln.name"
                        gap="2" align="center"
                    >
                        <span>{{ vuln.name || vuln.title }}</span>
                        <ui-badge v-if="vuln.impact" :label="vuln.impact" />
                    </ui-layout>
                </template>

                <ui-layout v-if="thirdparty.highest_vulnerability_impact" gap="2" align="center">
                    <strong>Highest Impact: </strong>
                    <ui-badge fill :label="String(thirdparty.highest_vulnerability_impact)" :color="trustColor(thirdparty.highest_vulnerability_impact)" />
                </ui-layout>
            </ui-layout>

            <!-- Trust Details -->
            <ui-layout
                direction="column"
                justify="normal"
                align="stretch"
            >
                <ui-layout align="center" gap="2">
                    <ui-heading size="md" tag="h3">Trust Relationships ({{ trustDetailsCount }})</ui-heading>
                    <ui-badge 
                        v-if="trustDetailsCount >= 10"
                        label="Sample shown"
                        color="blue"
                        fill
                    />
                </ui-layout>

                <template v-if="thirdparty.trust_details && thirdparty.trust_details.length">
                    <ui-layout
                        v-for="(detail, index) in thirdparty.trust_details"
                        :key="index"
                    >
                        <ui-badge fill :label="String(detail.trust_level)" :color="trustColor(detail.trust_level)" />

                        <ui-layout direction="column" gap="2">
                            <ui-layout align="center">
                                <span class="trust-type">{{ detail.trust_type }}</span>

                                <ui-badge
                                    :label="detailInstanceBadgeLabel(detail)"
                                />
                            </ui-layout>
                            <ui-layout v-if="detail.sample_instance" gap="2" align="center" wrap>
                                {{ detail.sample_instance.source_url }}
                                <ui-icon icon="fa-arrow-right" size="sm" />
                                {{ detail.sample_instance.target_url }}
                            </ui-layout>
                        </ui-layout>
                    </ui-layout>
                </template>

                <ui-heading v-else tag="p">No detailed trust information available</ui-heading>
            </ui-layout>

            <ui-layout>
                <ui-button
                    label="View Full Details"
                    @click.prevent="viewThirdpartyDetails"
                />
                <ui-button
                    v-if="$permissions.enable_active_checks_service"
                    label="Enable Active Monitor"
                    :url="activeChecksConfigUrl"
                    type="outline"
                />
            </ui-layout>
        </ui-panel-accordion>
        
    `
};

/**
 * Main Domain Details Component
 * Complete view showing all information about a domain and its third-party relationships
 */
export const ApckTrustDomainDetails = {
    components: {
        'apck-trust-level-badge': ApckTrustLevelBadge,
        'apck-domain-statistics': ApckDomainStatistics,
        'apck-thirdparty-item': ApckThirdpartyItem
    },
    props: {
        domainId: {
            type: String,
            required: true
        }
    },
    data: function() {
        return {
            loading: false,
            error: null,
            domainData: null,
            refreshInterval: null,
            sortBy: 'vulnerability', // trust_level, hostname, service_name, vulnerability
            sortOrder: 'desc', // asc, desc
            filterBy: this.$route?.query.filterBy || 'all', // all, highest, high, medium, low, info, investigate
            searchQuery: this.$route?.query.search || '',
            // Pagination for thirdparties
            thirdparties: [],
            thirdpartiesLoading: false,
            thirdpartiesPage: 1,
            thirdpartiesPerPage: 50,
            thirdpartiesTotalCount: 0,
            thirdpartiesHasMore: false
        };
    },
    computed: {
        hasData: function() {
            return this.domainData && this.domainData.success;
        },
        domain: function() {
            return this.hasData ? this.domainData.domain : {};
        },
        statistics: function() {
            return this.hasData ? this.domainData.statistics : {};
        },
        filteredAndSortedThirdparties: function() {
            let filtered = this.thirdparties;

            // Apply search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(tp =>
                    tp.hostname.toLowerCase().includes(query) ||
                    (tp.service_name && tp.service_name.toLowerCase().includes(query)) ||
                    (tp.parent_domain && tp.parent_domain.toLowerCase().includes(query))
                );
            }

            // Apply trust level filter
            if (this.filterBy !== 'all') {
                const levelMap = {
                    'highest': 5,
                    'high': 4,
                    'medium': 3,
                    'low': 2,
                    'info': 1,
                    'investigate': 0
                };
                if (levelMap.hasOwnProperty(this.filterBy)) {
                    filtered = filtered.filter(tp =>
                        tp.highest_trust_level?.level === levelMap[this.filterBy]
                    );
                }
            }

            // Apply sorting
            filtered = filtered.sort((a, b) => {
                let aValue, bValue;

                switch (this.sortBy) {
                    case 'trust_level':
                        aValue = a.highest_trust_level?.level || 0;
                        bValue = b.highest_trust_level?.level || 0;
                        break;
                    case 'hostname':
                        aValue = a.hostname || '';
                        bValue = b.hostname || '';
                        break;
                    case 'service_name':
                        aValue = a.service_name || 'Unknown';
                        bValue = b.service_name || 'Unknown';
                        break;
                    case 'vulnerability':
                        aValue = a.vulnerabilities?.length || 0;
                        bValue = b.vulnerabilities?.length || 0;
                        break;
                    default:
                        aValue = a.hostname || '';
                        bValue = b.hostname || '';
                }

                if (this.sortOrder === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });

            return filtered;
        },
        totalThirdparties: function() {
            return this.thirdpartiesTotalCount;
        },
        filteredCount: function() {
            return this.filteredAndSortedThirdparties.length;
        },
        trustFilterOptions() {
            return [
                { label: 'All Trust Levels', value: 'all' },
                { label: 'Highest (5)', value: 'highest' },
                { label: 'High (4)', value: 'high' },
                { label: 'Medium (3)', value: 'medium' },
                { label: 'Low (2)', value: 'low' },
                { label: 'Info (1)', value: 'info' },
                { label: 'Investigate (0)', value: 'investigate' }
            ]
        },
        trustFilterLabel() {
            const option = this.trustFilterOptions.find(
                (opt) => opt.value === this.filterBy
            )
            return option ? option.label : ''
        },
        sortByOptions() {
            return [
                { field: 'trust_level', label: 'Trust Level' },
                { field: 'hostname', label: 'Hostname' },
                { field: 'service_name', label: 'Service' },
                { field: 'vulnerability', label: 'Vulnerabilities' }
            ].map(option => ({
                ...option,
                icon: this.sortBy === option.field ? (this.sortOrder === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down') : null
            }));
        },
    },
    methods: {
        fetchData: function() {
            this.loading = true;
            this.error = null;

            fetch(`/trust/domain/${this.domainId}/details`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    this.domainData = data;
                    if (!data.success) {
                        this.error = data.message || 'Failed to load domain details';
                    } else {
                        // Now load first page of thirdparties
                        this.loadThirdparties(1);
                    }
                })
                .catch(error => {
                    console.error('Error fetching domain details:', error);
                    this.error = error.message || 'Failed to load domain details';
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadThirdparties: function(page) {
            this.thirdpartiesLoading = true;

            fetch(`/trust/domain/${this.domainId}/thirdparties?page=${page}&per_page=${this.thirdpartiesPerPage}`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    if (data.success) {
                        if (page === 1) {
                            // First page - replace array
                            this.thirdparties = data.thirdparties;
                        } else {
                            // Additional pages - append
                            this.thirdparties.push(...data.thirdparties);
                        }
                        this.thirdpartiesPage = data.page;
                        this.thirdpartiesTotalCount = data.total_count;
                        this.thirdpartiesHasMore = data.has_more;
                    }
                })
                .catch(error => {
                    console.error('Error fetching thirdparties:', error);
                })
                .finally(() => {
                    this.thirdpartiesLoading = false;
                });
        },
        loadMoreThirdparties: function() {
            if (!this.thirdpartiesHasMore || this.thirdpartiesLoading) {
                return;
            }
            this.loadThirdparties(this.thirdpartiesPage + 1);
        },
        refresh: function() {
            this.thirdparties = [];
            this.thirdpartiesPage = 1;
            this.thirdpartiesTotalCount = 0;
            this.thirdpartiesHasMore = false;
            this.fetchData();
        },
        formatDate: function(dateString) {
            if (!dateString) return 'N/A';
            dateString = `${dateString}000`; // Convert to milliseconds
            dateString = parseInt(dateString);
            return new Date(dateString).toLocaleString();
        },
        setSortBy: function(field) {
            if (this.sortBy === field) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortBy = field;
                this.sortOrder = 'desc';
            }
        },
        clearSearch: function() {
            this.searchQuery = '';
        },
        startAutoRefresh: function() {
            // Refresh every 5 minutes
            this.refreshInterval = setInterval(this.fetchData, 5 * 60 * 1000);
        },
        stopAutoRefresh: function() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        searchHandler(data) {
            this.searchQuery = data.value
        },
        trustFilterHandler(value) {
            this.filterBy = value
        }
    },
    mounted: function() {
        this.fetchData();
        this.startAutoRefresh();
    },
    beforeDestroy: function() {
        this.stopAutoRefresh();
    },
    watch: {
        domainId: function() {
            this.fetchData();
        },
        filterBy: function() {
            const query = { ...this.$route.query }

            if (!this.filterBy || this.filterBy === 'all') {
                delete query.filterBy
            } else {
                query.filterBy = this.filterBy
            }

            this.$router.push({
                path: this.$route.path,
                query
            })
        },
    },
    template: `
        <ui-page :title="'Domain Details: ' + (domain.hostname || 'Loading...')" stickyTitle>
            <template #content>
                <ui-spinner :show="loading && !hasData" theme="dark"/>

                <div v-if="error" class="error-container">
                    <apck-panel title="Error" icon="fa-exclamation-triangle">
                        <p>{{ error }}</p>
                        <ui-button
                            iconLeft="fa-refresh"
                            label="Refresh"
                            type="ghost"
                            hideLabel
                            size="sm"
                            :disabled="loading"
                            @click.prevent="refresh"
                        />
                    </apck-panel>
                </div>

                <ui-layout v-else-if="hasData" type="grid" cols="3" class="domain-details-content">
                    <!-- Domain Overview -->
                    <apck-panel title="Domain Overview" icon="fa-regular fa-info-circle">
                        <template v-slot:menu>
                            <ui-button
                                iconLeft="fa-refresh"
                                label="Refresh"
                                type="ghost"
                                hideLabel
                                size="sm"
                                :disabled="loading"
                                @click.prevent="refresh"
                            />
                        </template>

                        <ui-layout direction="column" gap="3">
                            <ui-layout gap="2" wrap>
                                <span class="text--secondary">Hostname:</span>
    
                                <ui-heading tag="span">{{ domain.hostname }}</ui-heading>
                            </ui-layout>

                            <ui-layout gap="2" wrap>
                                <span class="text--secondary">First Scanned:</span>
                                
                                <ui-heading tag="span"> {{ formatDate(domain.first_scanned_at) }}</ui-heading>
                            </ui-layout>
                            
                            <ui-layout gap="2" wrap>
                                <span class="text--secondary">Last Scanned:</span>
                                
                                <ui-heading tag="span">  {{ formatDate(domain.last_scanned_at) }}</ui-heading>
                            </ui-layout>

                            <ui-layout v-if="domain.executive_summary" direction="column" gap="2" wrap>
                                <span class="text--secondary">Executive Summary</span>
                                
                                <div class="summary-content" v-html="domain.executive_summary"></div>
                            </ui-layout>

                            <ui-layout v-if="domain.summary_of_findings" direction="column" gap="2" wrap>
                                <span class="text--secondary">Summary of Findings</span>
                                
                                <div class="findings-content" v-html="domain.summary_of_findings"></div>
                            </ui-layout>
                        </ui-layout>

                        <ui-spinner :show="loading" theme="dark"/>
                    </apck-panel>

                    <!-- Statistics -->
                    <apck-panel title="Statistics" icon="fa-regular fa-chart-bar" class="col-span-2">
                        <apck-domain-statistics :statistics="statistics" />
                    </apck-panel>

                    <!-- Third-parties List -->
                    <apck-panel title="Third-party Relationships" icon="fa-regular fa-network-wired" class="col-span-full">
                        <ui-layout direction="column" gap="6">
                            <ui-layout align="center" justify="space-between" gap="6" wrap>
                                <ui-layout align="center" class="no-shrink">
                                    <ui-heading tag="span">Filter by:</ui-heading>

                                    <!-- Filter by trust level -->
                                    <ui-dropdown-radio
                                        name="trustLevelFilter"
                                        :modelValue="filterBy"
                                        :options="trustFilterOptions"
                                        :label="trustFilterLabel"
                                        align="left"
                                        @update:modelValue="trustFilterHandler"
                                    />

                                    <!-- Search -->
                                    <ui-input-text
                                        label="Search third-parties..."
                                        placeholder="Search third-parties..."
                                        iconLeft="fa-magnifying-glass"
                                        type="search"
                                        hideLabel
                                        limitWidth
                                        @search="searchHandler"
                                    />
                                </ui-layout>

                                <ui-layout align="center" gap="2" class="no-shrink">
                                    <!-- Sort controls -->
                                    <ui-heading tag="div">Sort by:</ui-heading>

                                    <ui-button
                                        v-for="option in sortByOptions"
                                        :key="option.field"
                                        :iconRight="option.icon"
                                        :label="option.label"
                                        type="outline"
                                        @click.prevent="setSortBy(option.field)"
                                    />
                                </ui-layout>
                            </ui-layout>

                            <ui-layout direction="column">
                                <div class="results-info">
                                    <span>Showing {{ filteredCount }} of {{ totalThirdparties }} third-parties</span>
                                </div>

                                <div v-if="filteredAndSortedThirdparties.length === 0 && !thirdpartiesLoading" class="no-thirdparties">
                                    <p v-if="searchQuery || filterBy !== 'all'">
                                        <em>No third-parties match the current filters</em>
                                    </p>
                                    <p v-else>
                                        <em>No third-parties found for this domain</em>
                                    </p>
                                </div>

                                <template v-else>
                                    <apck-thirdparty-item
                                        v-for="thirdparty in filteredAndSortedThirdparties"
                                        :key="thirdparty.relationship_id"
                                        :thirdparty="thirdparty"
                                    />
                                </template>
                                
                                <ui-spinner :show="thirdpartiesLoading" theme="dark"/>
                                
                                <ui-layout v-if="thirdpartiesHasMore && !thirdpartiesLoading" justify="center">
                                    <ui-button
                                        label="Load More"
                                        iconLeft="fa-chevron-down"
                                        type="outline"
                                        @click.prevent="loadMoreThirdparties"
                                    />
                                </ui-layout>
                            </ui-layout>
                        </ui-layout>
                    </apck-panel>
                </ui-layout>
            </template>
        </ui-page>
    `
};
